import { useQuery } from '@tanstack/react-query';
import {
  type BankAccountResponse,
  formatMoney,
  fromCents,
  italianToday,
  type LedgerCycle,
  OPENING_STATE_LABELS,
  statementExport,
  type StatementExportSide,
  statementExportFileName,
  statementExportQuerySchema,
  statementInvoiceStateLabel,
  statementPaymentLabel,
  type StatementPrintedRequest,
  statementStateFilterLabel,
  type SubjectStatementResponse,
  toCents,
} from '@k-management/shared';
import { useEffect, useMemo, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useCurrentUser } from '../../auth/AuthProvider.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import '../quotes/documents/documents.css';
import { CreditNoteMark, creditNoteRow } from './treasury-ui.js';
import './statement-print.css';

const ROLE_LABELS: Record<LedgerCycle, string> = { attivo: 'cliente', passivo: 'fornitore' };

/**
 * The account statement in PDF (2.10, 19/10/2026), printed by the browser
 * with «Salva come PDF» like the documents of a quote: for internal use and
 * the accountant, without the ledgers. The sides, the filter and the sort come
 * in the address from the page of the statement; opening it goes in the
 * activity log, and with `?stampa` the browser is asked to print.
 */
export function StatementPrintPage() {
  const { id = '' } = useParams();
  const [search] = useSearchParams();
  const user = useCurrentUser();
  const statement = useQuery({
    queryKey: ['treasury', 'statements', id],
    queryFn: () => apiRequest<SubjectStatementResponse>(`/treasury/statements/${id}`),
  });
  const banks = useQuery({
    queryKey: ['treasury', 'banks'],
    queryFn: () => apiRequest<BankAccountResponse[]>('/treasury/banks'),
  });
  const options = useMemo(
    () => statementExportQuerySchema.safeParse(Object.fromEntries(search)),
    [search],
  );
  const data =
    statement.data && options.success ? statementExport(statement.data, options.data) : null;
  const today = italianToday();
  const ready = Boolean(data && banks.data);

  // The name the PDF is saved with.
  useEffect(() => {
    if (!data) return;
    document.title = statementExportFileName(data.subject.name, today);
    return () => {
      document.title = 'K-Management';
    };
  }, [data, today]);

  // Once per page, also when React runs the effects twice.
  const logged = useRef(false);
  useEffect(() => {
    if (!data || logged.current) return;
    logged.current = true;
    const body: StatementPrintedRequest = {
      cycles: data.sides.map((side) => side.cycle),
      state: data.state,
    };
    void apiRequest(`/treasury/statements/${id}/printed`, { method: 'POST', body });
  }, [data, id]);

  const autoPrint = search.has('stampa');
  useEffect(() => {
    if (!ready || !autoPrint) return;
    let cancelled = false;
    // The fonts first, or the PDF comes out in the fallback font.
    void document.fonts.ready.then(() => {
      if (!cancelled) window.print();
    });
    return () => {
      cancelled = true;
    };
  }, [ready, autoPrint]);

  const bankName = (code: string) => banks.data?.find((bank) => bank.code === code)?.alias ?? code;
  const now = new Date();
  return (
    <div className="doc-screen">
      <div className="doc-toolbar">
        <Link to={`/tesoreria/schede-contabili/${id}`}>← Torna alla scheda</Link>
        <span className="spacer" />
        <span className="muted small">Nella stampa scegli «Salva come PDF».</span>
        <button type="button" className="primary" onClick={() => window.print()}>
          Stampa o salva PDF
        </button>
      </div>
      {statement.isError ? (
        <p className="error">{statement.error.message}</p>
      ) : !options.success ? (
        <p className="error">Indirizzo della stampa non valido: riaprila dalla scheda.</p>
      ) : !data || !banks.data ? (
        <p>Caricamento…</p>
      ) : data.sides.length === 0 ? (
        <p className="notice info">Il soggetto non ha fatture in questo ruolo.</p>
      ) : (
        <article className="doc-page statement-print">
          <header className="doc-head">
            <img src="/k-rev-logo-arancio.png" alt="K-Rev" />
            <div className="doc-ref">
              <strong>Scheda contabile</strong>
              {data.subject.name} · {data.sides.map((side) => ROLE_LABELS[side.cycle]).join(' e ')}
              <br />
              {data.subject.vatNumber && <>P.IVA {data.subject.vatNumber} · </>}
              al {formatDate(today)} · fatture:{' '}
              {statementStateFilterLabel(
                data.state,
                data.sides.map((side) => side.cycle),
              )}
            </div>
          </header>

          {data.net && (
            <div className="statement-net">
              <strong>Posizione netta:</strong> crediti come cliente {formatMoney(data.net.credit)}{' '}
              − debiti come fornitore {formatMoney(data.net.debit)} ={' '}
              <strong>
                {formatMoney(fromCents(Math.abs(toCents(data.net.net))))}{' '}
                {toCents(data.net.net) < 0 ? 'a debito di K-Rev' : 'a credito di K-Rev'}
              </strong>
              <small>
                di cui scaduto: {formatMoney(data.net.overdueCredit)} a credito,{' '}
                {formatMoney(data.net.overdueDebit)} a debito. È solo un conto: nessuna
                compensazione registrata.
              </small>
            </div>
          )}

          {data.sides.map((side) => (
            <PrintedSide
              key={side.cycle}
              side={side}
              titled={data.sides.length > 1}
              bankName={bankName}
            />
          ))}

          {(data.financial.payments.length > 0 || data.financial.loans.length > 0) && (
            <section>
              <h2>Finanziaria</h2>
              {data.financial.payments.length > 0 && (
                <table>
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Banca</th>
                      <th>Pagamento o incasso senza documento</th>
                      <th className="numeric">Importo</th>
                      <th className="numeric">Disponibile</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.financial.payments.map((item) => (
                      <tr key={item.id}>
                        <td>{formatDate(item.date)}</td>
                        <td>{bankName(item.accountCode)}</td>
                        <td>
                          {item.direction === 'out' ? 'Pagato' : 'Incassato'}
                          {item.note ? ` · ${item.note}` : ''}
                        </td>
                        <td className="numeric">{formatMoney(item.amount)}</td>
                        <td className="numeric">{formatMoney(item.available)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {data.financial.loans.length > 0 && (
                <table>
                  <thead>
                    <tr>
                      <th>Finanziamento</th>
                      <th className="numeric">Erogato</th>
                      <th className="numeric">Restituito</th>
                      <th className="numeric">Residuo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.financial.loans.map((loan) => (
                      <tr key={loan.id}>
                        <td>{loan.name}</td>
                        <td className="numeric">{loan.amount ? formatMoney(loan.amount) : '—'}</td>
                        <td className="numeric">{formatMoney(loan.repaid)}</td>
                        <td className="numeric">
                          {loan.residual ? formatMoney(loan.residual) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          )}

          <footer className="statement-foot">
            K-Rev S.r.l. · stampato da K-Management il {formatDate(today)} alle{' '}
            {now.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })} da{' '}
            {`${user.firstName} ${user.lastName}`.trim() || user.email}
          </footer>
        </article>
      )}
    </div>
  );
}

function PrintedSide({
  side,
  titled,
  bankName,
}: {
  side: StatementExportSide;
  titled: boolean;
  bankName: (code: string) => string;
}) {
  const customer = side.cycle === 'attivo';
  return (
    <section className="statement-side">
      {titled && <h2>Come {ROLE_LABELS[side.cycle]}</h2>}
      <div className="statement-sums">
        {side.opening && (
          <div>
            <span>Saldo al 01/01/{side.opening.year}</span>
            {formatMoney(side.opening.balance)}
          </div>
        )}
        <div>
          <span>{customer ? 'Fatture emesse' : 'Fatture ricevute'} e partite iniziali</span>
          {formatMoney(side.totals.invoiced)}
        </div>
        <div>
          <span>{customer ? 'Incassato' : 'Pagato'}</span>
          {formatMoney(side.totals.settled)}
        </div>
        <div>
          <span>Aperto</span>
          <strong>{formatMoney(side.totals.residual)}</strong>
        </div>
        <div>
          <span>di cui scaduto</span>
          {formatMoney(side.totals.overdue)}
        </div>
      </div>

      {side.opening && side.opening.items.length > 0 && (
        <>
          <h3>
            Situazione al 01/01/{side.opening.year} · {OPENING_STATE_LABELS[side.opening.state]}
          </h3>
          <table className="doc-table">
            <thead>
              <tr>
                <th>Documento</th>
                <th className="num">Importo</th>
                <th className="num">Aperto</th>
                <th className="num">Residuo oggi</th>
                <th>Nell’anno</th>
              </tr>
            </thead>
            <tbody>
              {side.opening.items.map((item, index) => (
                <tr key={index}>
                  <td>
                    {item.kind === 'ante'
                      ? 'Saldo senza dettaglio'
                      : `Doc. ${item.documentNumber ?? '—'}${item.documentDate ? ` del ${formatDate(item.documentDate)}` : ''}`}
                  </td>
                  <td className="num">{formatMoney(item.amount)}</td>
                  <td className="num">{formatMoney(item.open)}</td>
                  <td className="num">
                    {item.residual === null ? '—' : formatMoney(item.residual)}
                  </td>
                  <td>{item.settledOn ? `chiusa il ${formatDate(item.settledOn)}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <h3>{customer ? 'Fatture emesse' : 'Fatture ricevute'}</h3>
      <table className="doc-table">
        <thead>
          <tr>
            <th>Fattura</th>
            <th>Scadenza</th>
            <th className="num">Da {customer ? 'incassare' : 'pagare'}</th>
            <th className="num">{customer ? 'Incassato' : 'Pagato'}</th>
            <th className="num">Residuo</th>
            <th>Stato</th>
            <th>{customer ? 'Incassi' : 'Pagamenti'}</th>
          </tr>
        </thead>
        <tbody>
          {side.invoices.map((item) => (
            <tr key={item.id} className={creditNoteRow(item)}>
              <td>
                <CreditNoteMark kind={item.kind} />
                {item.number}
                <small>
                  del {formatDate(item.date)}
                  {item.kind === 'proforma' && ' · pro-forma'}
                  {item.origin === 'apertura' && ' · situazione iniziale'}
                  {item.selfBilling && ' · autofattura (fuori dai totali)'}
                </small>
              </td>
              <td>{item.nextDue ? formatDate(item.nextDue) : '—'}</td>
              <td className="num">{formatMoney(item.netDue)}</td>
              <td className="num">
                {formatMoney(fromCents(toCents(item.netDue) - toCents(item.residual)))}
              </td>
              <td className="num">
                <strong>{formatMoney(item.residual)}</strong>
              </td>
              <td>{statementInvoiceStateLabel(item)}</td>
              <td className="statement-payments">
                {item.payments.length === 0
                  ? '—'
                  : item.payments.map((payment, index) => (
                      <span key={index}>
                        {formatDate(payment.date)} · {formatMoney(payment.amount)} ·{' '}
                        {statementPaymentLabel(payment, item, bankName)}
                      </span>
                    ))}
              </td>
            </tr>
          ))}
          {side.invoices.length === 0 && (
            <tr>
              <td colSpan={7}>Nessuna fattura con questo filtro.</td>
            </tr>
          )}
          <tr className="doc-strong">
            <td colSpan={2}>Totale</td>
            <td className="num">{formatMoney(side.shownTotals.invoiced)}</td>
            <td className="num">{formatMoney(side.shownTotals.settled)}</td>
            <td className="num">{formatMoney(side.shownTotals.residual)}</td>
            <td colSpan={2} />
          </tr>
        </tbody>
      </table>
    </section>
  );
}
